import { lazy, Suspense, useEffect, useRef, useState } from "react";

import { signIn } from "@/auth/sign-in";

import { AuthInput } from "@/components/ui/auth-input";
import { Button } from "@/components/ui/button";

const ReCaptcha = lazy(() => import("react-google-recaptcha"));

// Constants
const RECAPTCHA_KEY = import.meta.env.VITE_RECAPTCHA_KEY;

const STYLES = {
  button: {
    width: "108px",
    height: "32px",
    padding: "7px 0",
    background: "#0068B8",
    boxShadow: "0px 10px 10px rgba(0, 0, 0, 0.25)",
    fontSize: "15px",
    lineHeight: "15px",
    color: "#FFF8F8",
    borderRadius: "4px"
  }
} as const;

interface Credentials {
  username: string;
  password: string;
  recaptchaToken?: string;
}

const Login = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutMinutes, setLockoutMinutes] = useState(0);
  const [captchaKey, setCaptchaKey] = useState(0); // برای ریست کردن کپچا
  const recaptchaRef = useRef<any>(null);

  useEffect(() => {
    if (!RECAPTCHA_KEY) {
      setCaptchaVerified(true);
    }
  }, []);

  // بارگذاری وضعیت کپچا از localStorage هنگام mount
  useEffect(() => {
    const savedCaptchaState = localStorage.getItem("requiresCaptcha");
    if (savedCaptchaState === "true" && RECAPTCHA_KEY) {
      setShowCaptcha(true);
      setCaptchaVerified(false);
    }
  }, []);

  const checkLoginStatus = async (username: string) => {
    if (!username) return;

    try {
      const API_URL = import.meta.env.VITE_FORM_API_URL as string;
      const response = await fetch(
        `${API_URL}/api/Authentication/loginStatus/${username}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.status && result.data) {
          // اگر RECAPTCHA_KEY وجود ندارد، کپچا را نشان نده
          const shouldShowCaptcha =
            RECAPTCHA_KEY && result.data.requiresCaptcha;
          setShowCaptcha(shouldShowCaptcha);
          setIsLocked(result.data.isLocked);
          setLockoutMinutes(result.data.remainingLockoutMinutes || 0);

          // ذخیره وضعیت کپچا در localStorage
          if (shouldShowCaptcha) {
            localStorage.setItem("requiresCaptcha", "true");
          } else {
            localStorage.removeItem("requiresCaptcha");
          }

          // اگر کپچا لازم نیست یا RECAPTCHA_KEY وجود ندارد، آن را تأیید شده در نظر بگیر
          if (!shouldShowCaptcha) {
            setCaptchaVerified(true);
          } else {
            setCaptchaVerified(false);
          }
        }
      }
    } catch (error) {
      console.error("خطا در بررسی وضعیت لاگین:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const credentials = Object.fromEntries(formData) as unknown as Credentials;

    // Add captcha token to credentials if required
    if (showCaptcha && captchaToken) {
      credentials.recaptchaToken = captchaToken;
    }

    // اگر حساب قفل است
    if (isLocked) {
      alert(`حساب کاربری شما به مدت ${lockoutMinutes} دقیقه قفل شده است.`);
      return;
    }

    // اگر کپچا لازم است ولی تأیید نشده
    if (showCaptcha && !captchaVerified) {
      alert("لطفاً کپچا را تکمیل کنید");
      return;
    }

    setIsSubmitting(true);

    try {
      await signIn({ credentials });
      // لاگین موفق بود - پاک کردن وضعیت کپچا
      localStorage.removeItem("requiresCaptcha");
    } catch {
      // بعد از خطای لاگین، وضعیت جدید را بررسی کن
      await checkLoginStatus(credentials.username);

      // ریست کردن کپچا برای تلاش بعدی
      if (showCaptcha) {
        setCaptchaVerified(false);
        setCaptchaToken("");
        setCaptchaKey((prev) => prev + 1);
        // ریست کردن کپچا با استفاده از ref
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUsernameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const username = e.target.value;
    if (username) {
      checkLoginStatus(username);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-6 sm:space-y-8">
      <UsernameField onBlur={handleUsernameBlur} />
      <PasswordField />
      {isLocked && (
        <div className="text-center text-sm text-red-600">
          حساب کاربری شما به مدت {lockoutMinutes} دقیقه قفل شده است
        </div>
      )}
      {RECAPTCHA_KEY && showCaptcha && !isLocked && (
        <RecaptchaField
          key={captchaKey}
          recaptchaRef={recaptchaRef}
          onVerify={(token) => {
            setCaptchaToken(token || "");
            setCaptchaVerified(true);
          }}
        />
      )}
      <SubmitButton
        isSubmitting={isSubmitting}
        isDisabled={isLocked || (showCaptcha && !captchaVerified)}
      />
    </form>
  );
};

interface UsernameFieldProps {
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const UsernameField = ({ onBlur }: UsernameFieldProps) => (
  <AuthInput
    label="شناسه کاربری"
    name="username"
    type="text"
    labelWidth="82px"
    onBlur={onBlur}
    autoComplete="username"
  />
);

const PasswordField = () => (
  <AuthInput
    label="رمز عبور"
    name="password"
    type="password"
    labelWidth="49px"
    autoComplete="current-password"
  />
);

interface RecaptchaFieldProps {
  onVerify: (token: string | null) => void;
  recaptchaRef: React.RefObject<any>;
}

const RecaptchaField = ({ onVerify, recaptchaRef }: RecaptchaFieldProps) => (
  <div className="flex w-full justify-center">
    <Suspense fallback={<div className="h-20 w-full" />}>
      <ReCaptcha
        ref={recaptchaRef}
        sitekey={RECAPTCHA_KEY!}
        type="image"
        onChange={onVerify}
      />
    </Suspense>
  </div>
);

interface SubmitButtonProps {
  isSubmitting: boolean;
  isDisabled: boolean;
}

const SubmitButton = ({ isSubmitting, isDisabled }: SubmitButtonProps) => (
  <div className="flex justify-end">
    <Button
      type="submit"
      disabled={isSubmitting || isDisabled}
      className="flex items-center justify-center font-normal transition-opacity hover:opacity-90 disabled:opacity-50"
      style={STYLES.button}
    >
      {isSubmitting ? "..." : "ورود"}
    </Button>
  </div>
);

export default Login;
